/* eslint-disable no-underscore-dangle */
import {
  BreakpointObserver,
  BreakpointState,
  Breakpoints,
} from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ServiceSchedule } from '@app/core/api-azure-functions/dataverse-functions';
import { SupportPublicService } from '@app/core/api/api/support-public.service';
import { ServiceChangeModel } from '@app/core/models/case.model';
import { OptionModel } from '@app/core/models/option.model';
import { SiteModel } from '@app/core/models/site.model';
import { UserDataModel } from '@app/core/models/UserDataModel';
import { FileService } from '@app/core/services/file.service';
import { CustomSnackbarComponent } from '@app/custom-snackbar/custom-snackbar.component';
import {
  CASE_ORIGIN_CODE,
  RECURRING_SITE_TYPE,
  REGEX_PATTERN,
  SERVICE_CHANGE_TYPES,
  SUPPORT_TYPES,
} from '@app/shared/app.constants';
import { SupportTicketSubmitModalComponent } from '@app/shared/components/support-ticket-submit-modal/support-ticket-submit-modal.component';
import {
  AuthSelectors,
  CaseActions,
  CaseSelectors,
  GlobalActions,
  GlobalSelectors,
  SiteSelectors,
  UserDataSelectors,
} from '@app/store';
import { AppState } from '@app/store/app.state';
import { AccountInfo } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from 'rxjs/operators';

@Component({
  selector: 'app-service-change',
  templateUrl: './service-change.component.html',
  styleUrls: ['./service-change.component.scss'],
})
export class ServiceChangeComponent implements OnInit, OnDestroy {
  @ViewChild('picker', { static: true }) _picker: MatDatepicker<Date>;
  // handles all component subscriptions
  subscriptions: Subscription = new Subscription();
  isSitesLoading$: Observable<boolean> = this.store.select(
    SiteSelectors.isSitesLoading
  );
  isAllServiceSchedulesLoading$: Observable<boolean> = this.store.select(
    SiteSelectors.isAllServiceSchedulesLoading
  );

  userinfo$ = this.store.select(UserDataSelectors.getUserInfo);

  user: UserDataModel;
  caseTypeCode: string = SUPPORT_TYPES.SERVICE_CHANGE;
  supportCaseTypes: OptionModel[];
  allActiveSites: SiteModel[];
  allServiceSchedules: ServiceSchedule[] = [];
  initialValue: object = null;
  init = new Date();
  resetModel = new Date(0);
  isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(
    Breakpoints.XSmall
  );
  vertical: boolean = false;
  customerId: string;
  minDate: Date;
  maxDate: Date;
  disableSubmit = false;
  attachmentDetails: any[] = [];

  CLOSE_ON_SELECTED = true;
  MAX_NO_OF_DAYS = 3; // array starts with 0
  MESSAGE_MAX_LENGTH = 500;

  siteNameDropdownOptions: { value: string; text: string }[] = [];
  siteNameGUIDToAddressDictionary: Map<string, string> = new Map();
  wasteStreamDropdownOptions: { value: string; text: string }[];
  billingAccountDropdownOptions: { value: string; text: string }[] = [];
  equipmentTypeDropdownOptions: {
    value: string;
    text: string;
    options: string[];
  }[];
  equipmentSizeDropdownOptions: { value: string; text: string }[] = [];
  statesDropdownOptions: { value: string; text: string }[];
  filteredOptions: Observable<{ text: string; value: string }[]>;
  isSmsNotificationEnabled: boolean = false;
  isEmailNotificationEnabled: boolean = false;
  readonly SERVICE_CHANGE_TYPES = SERVICE_CHANGE_TYPES;
  readonly RECURRING_SITE_TYPE = RECURRING_SITE_TYPE;

  serviceChangeForm = this.fb.group({
    requestType: [SERVICE_CHANGE_TYPES.RECURRING, Validators.required],
    siteType: [RECURRING_SITE_TYPE.EXISTING_SITE, Validators.required],
    customerId: [''],
    siteId: ['', Validators.required],
    siteName: [''],
    address: [{ value: '', disabled: true }, Validators.required],
    city: [''],
    state: [''],
    zipcode: ['', [Validators.pattern(REGEX_PATTERN.ZIPCODE)]],
    name: [{ value: '', disabled: true }],
    email: [{ value: '', disabled: true }],
    phone: [
      '',
      [Validators.required, Validators.pattern(REGEX_PATTERN.PHONENUMBER)],
    ],
    additionalEmailAddress: ['', [Validators.maxLength(255)]],
    wasteStream: ['', Validators.required],
    equipmentType: ['', Validators.required],
    equipmentSize: [{ value: '', disabled: true }, Validators.required],
    pickupFrequency: ['', Validators.required],
    pickupWeekDays: [[]],
    pickupDates: [[]],
    fileUpload: [null],
    message: [
      '',
      [Validators.required, Validators.maxLength(this.MESSAGE_MAX_LENGTH)],
    ],
  });

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private supportPublicService: SupportPublicService,
    private breakpointObserver: BreakpointObserver,
    public modal: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private fileService: FileService
  ) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    this.minDate = new Date(currentYear, currentMonth, 1);
    this.maxDate = new Date(currentYear, currentMonth, 28);
  }

  ngOnInit(): void {
    this.getSites();
    this.getUserInfo();
    this.getServiceSchedules();
    this.watchSiteNameValueChange();
    this.getSupportCaseTypes();
    this.pickupFrequencyValueChanges();
    this.subscribeToCreatedCase();

    this.equipmentTypeDropdownOptions =
      this.supportPublicService.getEquipmentTypeOptions();
    this.watchEquipmentTypeValueChanges();

    this.statesDropdownOptions = this.supportPublicService.getStates('us');

    this.subscriptions.add(
      this.store
        .select(GlobalSelectors.getSelectedGlobalFiltersWithDetails)
        .pipe(
          filter(selectedBillingAcc => selectedBillingAcc.length > 0),
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe(selectedBillingAcc => {
          this.billingAccountDropdownOptions = [];

          selectedBillingAcc.map(billingAcc => {
            this.billingAccountDropdownOptions.push({
              value: billingAcc.id,
              text: billingAcc.name,
            });
          });
        })
    );

    const xsSubscription = this.isExtraSmall.subscribe(size => {
      if (size.matches) {
        this.vertical = true;
      } else {
        this.vertical = false;
      }
    });

    this.subscriptions.add(xsSubscription);
    // this.subscriptions.add(tenantSubscription);
    this.initialValue = this.serviceChangeForm.value;
  }

  openCustomSnackbar(
    message: string,
    actions: { action: () => void; actionLabel: string; color?: string }[]
  ) {
    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        message: message,
        actions: actions,
      },
      // duration: 5000,  // optional, auto-dismiss after 5 seconds
    });
  }

  handleGotoSettings() {
    this.router.navigate(['/account/account-information'], {
      queryParams: { active: 1 },
    });
  }

  handleClose() {
    //
  }

  ngOnDestroy(): void {
    this.snackBar.dismiss();
    this.subscriptions.unsubscribe();
  }

  get serviceSchedules() {
    return this.allServiceSchedules.filter(
      service =>
        service.accountId === this.serviceChangeForm.controls.siteId.value.value
    );
  }

  getServiceSchedules() {
    const serviceScheduleSubscription = this.store
      .select(SiteSelectors.getAllActiveServiceSchedules)
      .pipe(filter(response => response !== null && response.length > 0))
      .subscribe((allServices: ServiceSchedule[]) => {
        this.allServiceSchedules = allServices;
      });

    this.subscriptions.add(serviceScheduleSubscription);
  }

  onRequestTypeChange(value) {
    // Reset fields
    this.serviceChangeForm.reset(this.initialValue);
    this.getUserInfo();
    this.serviceChangeForm.controls.requestType.setValue(value);
    this.serviceChangeForm.controls.wasteStream.enable();
    this.serviceChangeForm.controls.equipmentType.enable();
    this.serviceChangeForm.controls.equipmentSize.enable();
    this.wasteStreamDropdownOptions = [];

    // Set defaults
    if (value === SERVICE_CHANGE_TYPES.SCHEDULE_CHANGE) {
      this.serviceChangeForm.controls.siteType.setValue(
        RECURRING_SITE_TYPE.EXISTING_SITE
      );
      this.onSiteTypeChange(RECURRING_SITE_TYPE.EXISTING_SITE);
      this.serviceChangeForm.controls.address.disable();
    } else if (value === SERVICE_CHANGE_TYPES.CANCELLATION) {
      // Cancellation
      this.serviceChangeForm.controls.siteType.setValue(
        SERVICE_CHANGE_TYPES.CANCEL_SERVICE
      );
      this.onSiteTypeChange(SERVICE_CHANGE_TYPES.CANCEL_SERVICE);
    }
  }

  get isNewSiteType() {
    return (
      this.serviceChangeForm.controls.siteType.value ===
      RECURRING_SITE_TYPE.NEW_SITE
    );
  }

  get isExistingSiteType() {
    return (
      this.serviceChangeForm.controls.siteType.value ===
      RECURRING_SITE_TYPE.EXISTING_SITE
    );
  }

  get isCancelSiteType() {
    return (
      this.serviceChangeForm.controls.siteType.value ===
      SERVICE_CHANGE_TYPES.CANCEL_SITE
    );
  }

  get isRecurringRequestType() {
    return (
      this.serviceChangeForm.controls.requestType.value ===
      SERVICE_CHANGE_TYPES.RECURRING
    );
  }

  get isCancellationRequestType() {
    return (
      this.serviceChangeForm.controls.requestType.value ===
      SERVICE_CHANGE_TYPES.CANCELLATION
    );
  }

  get isScheduleChangeRequestType() {
    return (
      this.serviceChangeForm.controls.requestType.value ===
      SERVICE_CHANGE_TYPES.SCHEDULE_CHANGE
    );
  }

  onSiteTypeChange(value: string) {
    // Reset fields
    this.resetFormFields([
      'customerId',
      'address',
      'siteName',
      'siteId',
      'city',
      'state',
      'zipcode',
      'fileUpload',
    ]);

    this.wasteStreamDropdownOptions = [];

    // Apply default validators
    this.setValidators(
      [
        'pickupFrequency',
        'wasteStream',
        'equipmentType',
        'equipmentSize',
        'siteId',
      ],
      [Validators.required]
    );
    this.serviceChangeForm.controls.equipmentType.setValidators([
      Validators.required,
      Validators.maxLength(this.MESSAGE_MAX_LENGTH),
    ]);

    switch (value) {
      case RECURRING_SITE_TYPE.NEW_SITE: // New site
        this.enableFields(['address']);
        this.setValidators(
          ['customerId', 'siteName', 'city', 'state', 'zipcode'],
          [Validators.required]
        );
        this.serviceChangeForm.controls.zipcode.setValidators([
          Validators.required,
          Validators.pattern(REGEX_PATTERN.ZIPCODE),
        ]);
        this.clearValidators(['siteId', 'fileUpload']);
        this.serviceChangeForm.controls.equipmentType.enable();
        this.updateWasteStreamDropdown();
        break;

      case SERVICE_CHANGE_TYPES.CANCEL_SITE: // Cancel Site
      case SERVICE_CHANGE_TYPES.CANCEL_SERVICE: // Cancel Service
        this.disableFields(['address']);
        this.clearValidators([
          'customerId',
          'siteName',
          'pickupFrequency',
          'city',
          'state',
          'zipcode',
          'fileUpload',
        ]);

        if (value === SERVICE_CHANGE_TYPES.CANCEL_SITE) {
          // Cancel Site
          this.setValidators(['fileUpload'], [Validators.required]);
          this.clearValidators([
            'wasteStream',
            'equipmentType',
            'equipmentSize',
          ]);
        }
        break;

      default:
        this.disableFields(['address']);
        this.clearValidators([
          'customerId',
          'siteName',
          'city',
          'state',
          'zipcode',
          'fileUpload',
        ]);
        this.setValidators(['siteId'], [Validators.required]);
        break;
    }

    this.updateFormValidity([
      'customerId',
      'siteName',
      'siteId',
      'address',
      'city',
      'state',
      'zipcode',
      'wasteStream',
      'equipmentType',
      'equipmentSize',
      'pickupFrequency',
      'message',
      'fileUpload',
    ]);
  }

  resetFormFields(fields: string[]) {
    fields.forEach(field =>
      this.serviceChangeForm.controls[field].setValue('')
    );
  }

  setValidators(fields: string[], validators: ValidatorFn[]) {
    fields.forEach(field =>
      this.serviceChangeForm.controls[field].setValidators(validators)
    );
  }

  clearValidators(fields: string[]) {
    fields.forEach(field =>
      this.serviceChangeForm.controls[field].setValidators([])
    );
  }

  enableFields(fields: string[]) {
    fields.forEach(field => this.serviceChangeForm.controls[field].enable());
  }

  disableFields(fields: string[]) {
    fields.forEach(field => this.serviceChangeForm.controls[field].disable());
  }

  updateFormValidity(fields: string[]) {
    fields.forEach(field =>
      this.serviceChangeForm.controls[field].updateValueAndValidity()
    );
  }

  watchEquipmentTypeValueChanges() {
    const equipmentTypeSubscription =
      this.serviceChangeForm.controls.equipmentType.valueChanges.subscribe(
        selectedValue => {
          this.serviceChangeForm.controls.equipmentSize.reset();
          if (!selectedValue) {
            this.serviceChangeForm.controls.equipmentSize.disable();
            this.equipmentSizeDropdownOptions = [];
          } else {
            this.serviceChangeForm.controls.equipmentSize.enable();
            const founSelectedEquipmentSizeOption =
              this.equipmentTypeDropdownOptions.find(
                equipmentTypeDropdownOption =>
                  equipmentTypeDropdownOption.value === selectedValue
              );

            if (founSelectedEquipmentSizeOption) {
              this.equipmentSizeDropdownOptions =
                founSelectedEquipmentSizeOption.options.map(str => ({
                  text: str,
                  value: str,
                }));

              // If there is only one size option then select the size directly
              if (this.equipmentSizeDropdownOptions.length === 1) {
                this.serviceChangeForm.controls.equipmentSize.setValue(
                  this.equipmentSizeDropdownOptions[0].value
                );
              }
            }
          }
        }
      );

    this.subscriptions.add(equipmentTypeSubscription);
  }

  updateWasteStreamDropdown() {
    this.wasteStreamDropdownOptions = [];

    // if schedules are available and its a Schedule Change
    if (this.isScheduleChangeRequestType) {
      if (this.serviceSchedules.length) {
        this.updateDropdownOptionsWithSchedules();
      }
    } else {
      this.updateDropdownOptionsWithoutSchedules();
    }

    this.updateValidationRules();
  }

  private updateDropdownOptionsWithSchedules() {
    this.serviceSchedules.forEach(service => {
      this.wasteStreamDropdownOptions.push({
        value: service.scheduleId,
        text: `${service.debris.toString()} - ${service.accountServiceEquipment.toString()}`,
      });
    });

    this.wasteStreamDropdownOptions.unshift({
      value: '',
      text: 'Select Waste Stream',
    });
  }

  private updateDropdownOptionsWithoutSchedules() {
    this.wasteStreamDropdownOptions =
      this.supportPublicService.getWasteStreamOptions();

    const foundOtherIndex = this.wasteStreamDropdownOptions.findIndex(
      item => item.value === 'Other'
    );

    if (foundOtherIndex === -1) {
      this.wasteStreamDropdownOptions.push({ value: 'Other', text: 'Other' });
    }

    this.equipmentTypeDropdownOptions =
      this.supportPublicService.getEquipmentTypeOptions();
  }

  private updateValidationRules() {
    // schedule change
    if (this.isScheduleChangeRequestType) {
      if (this.serviceSchedules.length) {
        this.serviceChangeForm.controls.wasteStream.enable();
        this.serviceChangeForm.controls.equipmentType.enable();
        this.serviceChangeForm.controls.equipmentSize.enable();

        this.serviceChangeForm.controls.wasteStream.setValidators([]);
        this.serviceChangeForm.controls.equipmentType.setValidators([]);
        this.serviceChangeForm.controls.equipmentSize.setValidators([]);
      } else {
        this.serviceChangeForm.controls.equipmentType.disable();
        this.serviceChangeForm.controls.wasteStream.setValidators([
          Validators.required,
        ]);
        this.serviceChangeForm.controls.equipmentType.setValidators([
          Validators.required,
        ]);
        this.serviceChangeForm.controls.equipmentSize.setValidators([
          Validators.required,
        ]);
      }
    } else {
      //      this.serviceChangeForm.controls.equipmentType.enable();
    }

    this.serviceChangeForm.controls.wasteStream.setValue('');
    this.serviceChangeForm.controls.equipmentType.setValue('');
    this.serviceChangeForm.controls.equipmentSize.setValue('');

    this.serviceChangeForm.controls.wasteStream.updateValueAndValidity();
    this.serviceChangeForm.controls.equipmentType.updateValueAndValidity();
    this.serviceChangeForm.controls.equipmentSize.updateValueAndValidity();
  }

  watchSiteNameValueChange() {
    this.filteredOptions =
      this.serviceChangeForm.controls.siteId.valueChanges.pipe(
        startWith(''),
        map(value => (typeof value === 'string' ? value : value.text)),
        map(text =>
          // eslint-disable-next-line no-underscore-dangle
          text ? this._filter(text) : this.siteNameDropdownOptions.slice()
        )
      );

    const siteNameSubscription = this.serviceChangeForm
      .get('siteId')
      .valueChanges.pipe(debounceTime(400))
      .subscribe((option: { text: string; value: string }) => {
        // make sure a site is selected before continuing
        if (option && option.value && option.value.length) {
          // set address
          const siteAddress = this.siteNameGUIDToAddressDictionary.get(
            option.value
          );

          this.serviceChangeForm.controls.address.setValue(siteAddress);
          this.updateWasteStreamDropdown();
        } else {
          // no site selected, reset address
          this.serviceChangeForm.controls.address.setValue('');
        }
      });

    this.serviceChangeForm.controls.siteId.setValidators(
      this.isInvalidSiteValidator()
    );
    this.subscriptions.add(siteNameSubscription);
  }

  pickupFrequencyValueChanges() {
    this.serviceChangeForm
      .get('pickupFrequency')
      .valueChanges.subscribe(value => {
        // Reset all dependency
        this.serviceChangeForm.controls.pickupWeekDays.setValue([]);
        this.serviceChangeForm.controls.pickupDates.setValue([]);

        this.serviceChangeForm.controls.pickupWeekDays.setValidators([]);
        this.serviceChangeForm.controls.pickupDates.setValidators([]);

        if (value === 'Weekly' || value === 'BI-Weekly') {
          this.serviceChangeForm.controls.pickupWeekDays.setValidators([
            Validators.required,
          ]);
        } else if (value === 'Monthly') {
          this.serviceChangeForm.controls.pickupDates.setValidators([
            Validators.required,
          ]);
        }

        this.serviceChangeForm.controls.pickupWeekDays.updateValueAndValidity();
        this.serviceChangeForm.controls.pickupDates.updateValueAndValidity();
        this.serviceChangeForm.controls.message.updateValueAndValidity();
      });
  }

  getSites() {
    const sitesSubscription = this.store
      .select(SiteSelectors.getAllActiveSites)
      .subscribe((allActiveSites: SiteModel[]) => {
        if (allActiveSites !== undefined) {
          allActiveSites.forEach(site => {
            this.siteNameDropdownOptions.push({
              value: site.accountId,
              text: site.name,
            });
            this.siteNameGUIDToAddressDictionary.set(
              site.accountId,
              site.address1 +
                ', ' +
                site.city +
                ', ' +
                site.state +
                ' ' +
                site.zip
            );
          });
          // save all site data for later
          this.allActiveSites = allActiveSites;

          this.serviceChangeForm.controls.siteId.setValue('');
        }
      });

    this.subscriptions.add(sitesSubscription);
  }

  // eslint-disable-next-line no-underscore-dangle
  private _filter(value: string): { text: string; value: string }[] {
    const filterValue = value.toLowerCase();

    return this.siteNameDropdownOptions.filter(option =>
      option.text.toLowerCase().includes(filterValue)
    );
  }

  displayFn(option: { text: string; value: string }): string {
    return option && option.text ? option.text : '';
  }

  isInvalidSiteValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const controlValue = control.value;
      // check if controlValue is not empty and equal to one of the option
      const index =
        typeof controlValue === 'object' && controlValue.value !== ''
          ? this.siteNameDropdownOptions.findIndex(
              option => option.value === controlValue.value
            )
          : -1;

      return index < 0
        ? {
            inValidSite: {
              value:
                controlValue === 'object' ? controlValue.value : controlValue,
            },
          }
        : null;
    };
  }

  get changeTypeCode(): number {
    if (this.isScheduleChangeRequestType) {
      return Number(SERVICE_CHANGE_TYPES.SCHEDULE_CHANGE);
    }
    if (this.isCancellationRequestType) {
      // Schedule Change
      return undefined;
    }
    // Reccuring
    return Number(this.serviceChangeForm.controls.siteType.value);
  }

  get subCaseTypeCode(): number {
    // Cancellation
    if (!this.isCancellationRequestType) {
      // Schedule Change
      return undefined;
    }

    // Reccuring
    return Number(this.serviceChangeForm.controls.siteType.value);
  }

  public dateClass = (date: Date) => {
    // eslint-disable-next-line no-underscore-dangle
    if (this._findDate(date) !== -1) {
      return ['selected'];
    }
    return [];
  };

  public getDay(date: Date): number {
    const dateObj = new Date(date);

    return dateObj.getDate();
  }

  public dateChanged(event: MatDatepickerInputEvent<Date>): void {
    if (
      event.value &&
      this.serviceChangeForm.controls.pickupDates.value.length <=
        this.MAX_NO_OF_DAYS
    ) {
      const date = event.value;
      // eslint-disable-next-line no-underscore-dangle
      const index = this._findDate(date);

      if (index === -1) {
        const pickupDates = this.serviceChangeForm.controls.pickupDates.value;

        pickupDates.push(date);
        this.serviceChangeForm.controls.pickupDates.setValue(pickupDates);
      } else {
        const pickupDates = this.serviceChangeForm.controls.pickupDates.value;

        pickupDates.splice(index, 1);
        this.serviceChangeForm.controls.pickupDates.setValue(pickupDates);
      }
      this.resetModel = new Date(0);
      if (!this.CLOSE_ON_SELECTED) {
        const closeFn = this._picker.close;

        this._picker.close = () => {
          //
        };
        this._picker[
          // eslint-disable-next-line dot-notation
          '_popupComponentRef'
        ].instance._calendar.monthView._createWeekCells();
        setTimeout(() => {
          this._picker.close = closeFn;
        });
      }
    }
  }

  public remove(date: Date): void {
    // eslint-disable-next-line no-underscore-dangle
    const index = this._findDate(date);

    const pickupDates = this.serviceChangeForm.controls.pickupDates.value;

    pickupDates.splice(index, 1);
    this.serviceChangeForm.controls.pickupDates.setValue(pickupDates);
  }

  private _findDate(date: Date): number {
    return this.serviceChangeForm.controls.pickupDates.value
      .map(m => +m)
      .indexOf(+date);
  }

  getSelectedSite(): SiteModel {
    return this.allActiveSites.find(
      site =>
        site.accountId === this.serviceChangeForm.controls.siteId.value.value
    );
  }

  getSelectedServiceSchedule(): ServiceSchedule {
    return this.allServiceSchedules.find(
      schedule =>
        schedule.scheduleId ===
        this.serviceChangeForm.controls.wasteStream.value
    );
  }

  getSupportCaseTypes() {
    this.store.dispatch(CaseActions.getCaseTypes());
    const supportTypeSubscription = this.store
      .select(CaseSelectors.getOrderedSupportCaseTypes)
      .subscribe((supportCaseTypes: OptionModel[]) => {
        if (supportCaseTypes) {
          this.supportCaseTypes = supportCaseTypes;
        }
      });

    this.subscriptions.add(supportTypeSubscription);
  }

  getCaseTypeName(): string {
    const caseType = this.supportCaseTypes.find(
      supportCaseType => supportCaseType.id === this.caseTypeCode
    );

    return caseType?.name || '';
  }

  /// File upload /////////////////////////////////
  setFiles(files) {
    this.attachmentDetails = [];

    if (files && files.length > 0) {
      // incase the string takes a long time to generate, wait until generated
      // disable submit button while base64 is generating
      this.disableSubmit = true;
      // set file to the form control for form validation
      // Store base64 strings for all files
      const fileUploadPromises = files.map(file =>
        this.fileService.getBase64(file).then((result: string) => {
          // Extract only the base64 string

          this.attachmentDetails.push({
            attachmentFileName: file.name,
            attachmentContentType: file.type,
            attachmentContent: result.split(';base64,')[1],
          });
        })
      );

      // Wait for all files to be processed
      Promise.all(fileUploadPromises).then(() => {
        // Set all files and their base64 strings to the form control
        this.serviceChangeForm.controls.fileUpload.setValue(files);
        this.disableSubmit = false;
      });
    } else {
      // reset to null and mark field as invalid by resetting the required validator
      this.serviceChangeForm.controls.fileUpload.setValue(null);
      this.serviceChangeForm.controls.fileUpload.updateValueAndValidity();
    }

    this.serviceChangeForm.controls.fileUpload.updateValueAndValidity();
  }

  getUserInfo() {
    this.subscriptions.add(
      this.store
        .select(AuthSelectors.getUser)
        .subscribe((user: AccountInfo) => {
          if (user !== null) {
            this.serviceChangeForm.controls.email.setValue(
              user.idTokenClaims.email
            );
            this.serviceChangeForm.controls.name.setValue(user.name);
          }
        })
    );

    this.subscriptions.add(
      this.userinfo$.subscribe((user: UserDataModel) => {
        if (user !== null && user.adObjectId) {
          this.user = user;
          // Set phone number
          this.serviceChangeForm.controls.phone.setValue(user.phoneNumber);

          const notifications = this.user?.notificationSettings;
          const caseUpdatesNotifications = notifications?.caseUpdates;

          // Is SMS or Email notifications are enabled
          this.isSmsNotificationEnabled = !!(
            this.user?.phoneNumber &&
            this.user?.isPhoneValidated &&
            caseUpdatesNotifications?.sms
          );

          this.isEmailNotificationEnabled = !!caseUpdatesNotifications?.email;

          const isValidNotificationSet =
            this.isSmsNotificationEnabled || this.isEmailNotificationEnabled;

          // Get notification type
          const notificationType = (() => {
            if (
              this.isSmsNotificationEnabled &&
              this.isEmailNotificationEnabled
            ) {
              return 'SMS and Email';
            }
            if (this.isSmsNotificationEnabled) {
              return 'SMS';
            }
            if (this.isEmailNotificationEnabled) {
              return 'Email';
            }
            return 'N/A';
          })();

          this.openCustomSnackbar(
            isValidNotificationSet
              ? `You will receive notifications via ${notificationType} for updates related to this case.`
              : `Want to receive case updates via SMS, Email ? Opt in for notifications.`,
            isValidNotificationSet
              ? [
                  {
                    action: () => this.handleClose(),
                    actionLabel: 'Close',
                    color: 'accent',
                  },
                ]
              : [
                  {
                    action: () => this.handleGotoSettings(),
                    actionLabel: 'Set Notifications',
                    color: 'accent',
                  },
                  {
                    action: () => this.handleClose(),
                    actionLabel: 'Close',
                    color: 'warn',
                  },
                ]
          );
        }
      })
    );
  }

  get addNewToLabel() {
    return this.serviceChangeForm.controls.siteId.value &&
      this.serviceChangeForm.controls.siteId.value.value &&
      this.isScheduleChangeRequestType &&
      this.serviceSchedules.length > 0
      ? 'New '
      : '';
  }

  onSubmit() {
    // Do not delete below code (invalid fields finder)
    // const invalid = [];
    // const { controls } = this.serviceChangeForm;

    // for (const name in controls) {
    //   if (controls[name].invalid) {
    //     invalid.push(name);
    //   }
    // }
    // console.log('Invalid fields>>', invalid);

    if (this.serviceChangeForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    const caseTypeName = this.getCaseTypeName();

    const selectedSite:
      | SiteModel
      | { name: string; rtsCompanyId: string | undefined } = this.isNewSiteType
      ? {
          name: this.serviceChangeForm.controls.siteName.value,
          rtsCompanyId: undefined,
        }
      : this.getSelectedSite();
    const selectedServiceSchedule: ServiceSchedule =
      this.getSelectedServiceSchedule();
    const isServiceScheduleSelected =
      selectedServiceSchedule && selectedServiceSchedule.scheduleId;
    const pickupFrequency =
      this.serviceChangeForm.controls.pickupFrequency.value;

    // eslint-disable-next-line max-len
    let description = `Waste Stream: ${this.serviceChangeForm.controls.wasteStream.value} - ${this.serviceChangeForm.controls.equipmentType.value} - ${this.serviceChangeForm.controls.equipmentSize.value} `;

    description += `\n\nPickup Frequency: ${pickupFrequency}`;

    if (pickupFrequency === 'Weekly' || pickupFrequency === 'BI-Weekly') {
      description += `\n\nPickup Week Days: ${this.serviceChangeForm.controls.pickupWeekDays.value}`;
    } else if (pickupFrequency === 'Monthly') {
      const pickupDates = this.serviceChangeForm.controls.pickupDates.value.map(
        dates => this.getDay(dates)
      );

      description += `\n\nPickup Dates: ${pickupDates.join(', ')}`;
    }

    if (this.serviceChangeForm.controls.message.value.length) {
      description += `\n\nMessage: ${this.serviceChangeForm.controls.message.value}`;
    }

    const newCase: ServiceChangeModel = {
      title: `${caseTypeName} - ${selectedSite.name}`,
      usersName: this.serviceChangeForm.controls.name.value,
      caseTypeCode: Number(this.caseTypeCode),
      subCaseTypeCode: this.subCaseTypeCode,
      caseOriginCode: CASE_ORIGIN_CODE.CUSTOMER_PORTAL,
      description: description,
      attachments:
        this.isCancellationRequestType && this.isCancelSiteType
          ? this.attachmentDetails
          : undefined,
      rtsCompanyId: selectedSite.rtsCompanyId,
      scheduleAId: isServiceScheduleSelected
        ? selectedServiceSchedule.scheduleId.toString()
        : '00000000-0000-0000-0000-000000000000',
      accountId:
        this.serviceChangeForm.controls.siteId.value.value ||
        this.serviceChangeForm.controls.customerId.value,
      customerId: this.customerId,
      changeTypeCode: this.changeTypeCode,
      wasteStream: isServiceScheduleSelected
        ? undefined
        : this.serviceChangeForm.controls.wasteStream.value,
      equipmentType: this.serviceChangeForm.controls.equipmentType.value,
      // eslint-disable-next-line no-nested-ternary
      equipmentSize: this.serviceChangeForm.controls.equipmentSize.value,
      streetAddress: this.isNewSiteType
        ? this.serviceChangeForm.controls.address.value
        : undefined,
      city: this.isNewSiteType
        ? this.serviceChangeForm.controls.city.value
        : undefined,
      state: this.isNewSiteType
        ? this.serviceChangeForm.controls.state.value
        : undefined,
      zip: this.isNewSiteType
        ? this.serviceChangeForm.controls.zipcode.value
        : undefined,
      phoneNumber: this.serviceChangeForm.controls.phone.value,
      emailAddress: this.serviceChangeForm.controls.email.value,
      additionalEmailAddress: this.isEmailNotificationEnabled
        ? this.serviceChangeForm.controls.additionalEmailAddress.value
        : undefined,
      emailNotification: this.isEmailNotificationEnabled,
      sms: this.isSmsNotificationEnabled,
    };

    this.store.dispatch(GlobalActions.showSpinner());
    this.store.dispatch(CaseActions.createCase({ newCase: newCase }));
  }

  markAllFieldsAsTouched() {
    Object.keys(this.serviceChangeForm.controls).forEach(field => {
      const control = this.serviceChangeForm.get(field);

      control.markAsTouched({ onlySelf: true });
    });
  }

  onServiceChangeFormCancel() {
    this.serviceChangeForm.reset(this.initialValue);
    this.getUserInfo();
    this.serviceChangeForm.controls.equipmentType.enable();
    this.serviceChangeForm.markAsPristine();
    this.serviceChangeForm.markAsTouched();
  }

  subscribeToCreatedCase() {
    const createdCaseSubscription = this.store
      .select(CaseSelectors.getCreatedCaseNumber)
      .subscribe(createdCaseNumber => {
        if (createdCaseNumber) {
          // hide spinner when new caseNumber is returned
          this.store.dispatch(GlobalActions.hideSpinner());
          this.modal.open(SupportTicketSubmitModalComponent, {
            disableClose: true,
            data: {
              caseNumber: createdCaseNumber,
              rerouteUrl: '/support-signed-in/support-ticket-list',
            },
          });
        }
      });

    this.subscriptions.add(createdCaseSubscription);
  }
}
